import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Check, X, ChevronRight, ChevronDown, Truck } from 'lucide-react';
import CitySelect from './CitySelect';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Subcategory {
  id: number;
  name: string;
  parent_id: number;
}

interface SearchFiltersProps {
  minPrice: number;
  maxPrice: number;
  onPriceChange: (min: number, max: number) => void;
  onCityChange: (city: string) => void;
  onCategoryChange: (categoryId: string) => void;
  onSubcategoryChange?: (subcategoryId: string) => void;
  selectedCity: string;
  selectedCategoryId: string;
  selectedSubcategoryId?: string;
  onClear: () => void;
  isFiltered: boolean;
  
  // Vehicle-specific filters
  onMakeChange?: (make: string) => void;
  onYearChange?: (minYear: number, maxYear: number) => void;
  selectedMake?: string;
  minYear?: number;
  maxYear?: number;
  
  // Transport filter
  onTransportOptionChange?: (option: string) => void;
  selectedTransportOption?: string;
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1970;

const SearchFilters = ({
  minPrice,
  maxPrice,
  onPriceChange,
  onCityChange,
  onCategoryChange,
  onSubcategoryChange,
  selectedCity,
  selectedCategoryId,
  selectedSubcategoryId,
  onClear,
  isFiltered,
  onMakeChange,
  onYearChange,
  selectedMake,
  minYear = MIN_YEAR,
  maxYear = CURRENT_YEAR,
  onTransportOptionChange,
  selectedTransportOption = ''
}: SearchFiltersProps) => {
  const [priceRange, setPriceRange] = useState<[number, number]>([minPrice, maxPrice]);
  const [yearRange, setYearRange] = useState<[number, number]>([minYear, maxYear]);
  const [selectedCountry, setSelectedCountry] = useState<string>('albania');
  
  // Fetch categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }
      
     
      return data || [];
    },
  });

  // Set categories from query result
  const categories = categoriesData || [];

  // Fetch subcategories for the selected category
  const { data: subcategoriesData, isLoading: isLoadingSubcategories } = useQuery({
    queryKey: ['subcategories', selectedCategoryId],
    queryFn: async () => {
      if (!selectedCategoryId) return [];
      
      // Make sure the categoryId is a number
      const categoryIdNum = parseInt(selectedCategoryId, 10);
      if (isNaN(categoryIdNum)) {
        console.error('Invalid category ID:', selectedCategoryId);
        return [];
      }
      
   
      
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('parent_id', categoryIdNum)
        .order('name');
      
      if (error) {
        console.error('Error fetching subcategories:', error);
        throw error;
      }
      
      console.log('Subcategories loaded:', data ? data.length : 0, 'items');
      if (data && data.length > 0) {
        console.log('First subcategory:', data[0]);
      } else {
        console.log('No subcategories found for category', categoryIdNum);
      }
      
      return data || [];
    },
    enabled: !!selectedCategoryId,
  });

  // Set subcategories from query result
  const subcategories = subcategoriesData || [];

  // Fetch vehicle makes for automobile category
  const { data: vehicleMakes } = useQuery({
    queryKey: ['vehicleMakes', selectedCategoryId],
    queryFn: async () => {
      if (selectedCategoryId !== '7') return []; // Only fetch for automobile category (ID 7)
      
      const { data, error } = await supabase
        .from('listings')
        .select('make')
        .eq('category_id', 7)
        .not('make', 'is', null)
        .order('make');
      
      if (error) throw error;
      
      // Extract unique makes
      const uniqueMakes = [...new Set(data.map(item => item.make))].filter(Boolean);
      return uniqueMakes.map(make => ({ name: make }));
    },
    enabled: selectedCategoryId === '7',
  });

  // Reset local state when filters are cleared externally
  useEffect(() => {
    setPriceRange([minPrice, maxPrice]);
    setYearRange([minYear, maxYear]);
  }, [isFiltered, minPrice, maxPrice, minYear, maxYear]);

  // Auto-apply price filter when price range changes
  useEffect(() => {
    // Apply with a small delay to avoid too frequent updates
    const timer = setTimeout(() => {
      onPriceChange(priceRange[0], priceRange[1]);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [priceRange, onPriceChange]);

  // Auto-apply year filter when year range changes
  useEffect(() => {
    if (onYearChange) {
      const timer = setTimeout(() => {
        onYearChange(yearRange[0], yearRange[1]);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [yearRange, onYearChange]);

  const handlePriceChange = (values: number[]) => {
    const [min, max] = values as [number, number];
    setPriceRange([min, max]);
  };

  const handleYearChange = (values: number[]) => {
    const [min, max] = values as [number, number];
    setYearRange([min, max]);
  };

  // Handle category change
  const handleCategoryChange = (categoryId: string) => {
    onCategoryChange(categoryId);
    // Reset subcategory when category changes
    if (onSubcategoryChange) {
      onSubcategoryChange('');
    }
  };

  // Handle transport option change
  const handleTransportOptionChange = (value: string) => {
    if (onTransportOptionChange) {
      onTransportOptionChange(value);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setPriceRange([minPrice, maxPrice]);
    if (onYearChange) {
      setYearRange([MIN_YEAR, CURRENT_YEAR]);
    }
    onClear();
  };

  // Handle country change
  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    // Reset city when country changes
    onCityChange('');
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <ScrollArea className="h-full max-h-[calc(80vh-100px)] pr-3 overflow-y-auto">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Filtro sipas çmimit</h3>
                {isFiltered && (
                  <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-8 px-2">
                    <X size={16} className="mr-1" /> Pastro
                  </Button>
                )}
              </div>
              
              <div className="pt-4 px-2">
                <Slider
                  defaultValue={[minPrice, maxPrice]}
                  max={10000}
                  step={100}
                  value={priceRange}
                  onValueChange={handlePriceChange}
                  className="mb-6"
                />
                
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{priceRange[0]} €</span>
                  <span>{priceRange[1]} €</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">Filtro sipas kategorisë</h3>
              <div className="space-y-2 mt-2">
                {isLoadingCategories ? (
                  <div className="p-2 text-gray-500">Duke ngarkuar...</div>
                ) : categories.length > 0 ? (
                  categories.map((category) => (
                    <div key={category.id} className="space-y-1">
                      <div 
                        className={`p-2 rounded-md cursor-pointer transition-colors ${
                          selectedCategoryId === category.id.toString() 
                            ? 'bg-brand-50 text-brand-700 font-medium' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleCategoryChange(category.id.toString())}
                      >
                        <div className="flex items-center justify-between">
                          <span>{category.name}</span>
                          {selectedCategoryId === category.id.toString() && (
                            <Check size={16} className="text-brand-500 mr-1" />
                          )}
                        </div>
                      </div>

                      {/* Show subcategories if category is selected */}
                      {selectedCategoryId === category.id.toString() && onSubcategoryChange && (
                        <div className="ml-4 mt-2 space-y-1">
                          {isLoadingSubcategories ? (
                            <div className="p-2 text-gray-500">Duke ngarkuar...</div>
                          ) : subcategories.length > 0 ? (
                            subcategories.map((subcategory) => (
                              <div 
                                key={subcategory.id}
                                className={`p-2 rounded-md cursor-pointer transition-colors text-sm ${
                                  selectedSubcategoryId === subcategory.id.toString() 
                                    ? 'bg-brand-50 text-brand-700 font-medium' 
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => onSubcategoryChange(subcategory.id.toString())}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{subcategory.name}</span>
                                  {selectedSubcategoryId === subcategory.id.toString() && (
                                    <Check size={14} className="text-brand-500" />
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-2 text-gray-500">Nuk ka nënkategori</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-gray-500">Nuk ka kategori</div>
                )}
              </div>
            </div>
            
            {/* Transport filter */}
            <div>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="transport-filter">
                  <AccordionTrigger className="font-medium">
                    <div className="flex items-center">
                      <Truck className="mr-2 h-4 w-4" />
                      Opsionet e transportit
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <RadioGroup 
                      value={selectedTransportOption} 
                      onValueChange={handleTransportOptionChange}
                      className="space-y-2 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="" id="transport-all" />
                        <Label htmlFor="transport-all">Të gjitha</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="shipping_and_pickup" id="transport-both" />
                        <Label htmlFor="transport-both">Transport & Tërheqje</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pickup_only" id="transport-pickup" />
                        <Label htmlFor="transport-pickup">Vetëm Tërheqje</Label>
                      </div>
                    </RadioGroup>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {/* Vehicle-specific filters */}
            {selectedCategoryId === '2' && onMakeChange && (
              <div>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="vehicle-filters">
                    <AccordionTrigger className="font-medium">Filtrat e automjeteve</AccordionTrigger>
                    <AccordionContent>
                      {/* Make filter */}
                      {vehicleMakes && vehicleMakes.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium mb-2">Marka</h4>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {vehicleMakes.map((make: { name: string }) => (
                              <div 
                                key={make.name}
                                className={`p-2 rounded-md cursor-pointer transition-colors text-sm ${
                                  selectedMake === make.name 
                                    ? 'bg-brand-50 text-brand-700 font-medium' 
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => onMakeChange(make.name)}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{make.name}</span>
                                  {selectedMake === make.name && (
                                    <Check size={14} className="text-brand-500" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Year range filter */}
                      {onYearChange && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium mb-2">Viti i prodhimit</h4>
                          <div className="pt-4 px-2">
                            <Slider
                              defaultValue={[MIN_YEAR, CURRENT_YEAR]}
                              min={MIN_YEAR}
                              max={CURRENT_YEAR}
                              step={1}
                              value={yearRange}
                              onValueChange={handleYearChange}
                              className="mb-6"
                            />
                            
                            <div className="flex justify-between text-sm text-gray-500">
                              <span>{yearRange[0]}</span>
                              <span>{yearRange[1]}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}
            
            <div>
              <h3 className="font-medium mb-2">Filtro sipas shtetit</h3>
              <Select value={selectedCountry} onValueChange={handleCountryChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Zgjidhni shtetin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="albania">Shqipëri</SelectItem>
                  <SelectItem value="kosovo">Kosovë</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">Filtro sipas qytetit</h3>
              <CitySelect 
                value={selectedCity} 
                onChange={onCityChange}
                placeholder="Të gjitha qytetet"
                country={selectedCountry}
              />
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default SearchFilters;
